import { MessageSquare, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useChatMessages } from '@chainlit/react-client';

import { type Conversation, conversationsApi } from '@/api/conversations';
import { cn } from '@/lib/utils';
import { Loader } from '@/components/Loader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem
} from '@/components/ui/sidebar';

const PAGE_SIZE = 40;

function groupByDate(items: Conversation[]): Record<string, Conversation[]> {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
  const start7 = new Date(startOfToday.getTime() - 6 * 86400000);
  const start30 = new Date(startOfToday.getTime() - 29 * 86400000);

  const groups: Record<string, Conversation[]> = {};
  const push = (key: string, item: Conversation) => {
    (groups[key] ??= []).push(item);
  };

  for (const item of items) {
    const d = new Date(item.updated_at);
    if (d >= startOfToday) push('今天', item);
    else if (d >= startOfYesterday) push('昨天', item);
    else if (d >= start7) push('過去 7 天', item);
    else if (d >= start30) push('過去 30 天', item);
    else push(String(d.getFullYear()), item);
  }
  return groups;
}

const GROUP_ORDER = ['今天', '昨天', '過去 7 天', '過去 30 天'];

interface Props {
  onSelect?: () => void;
}

export function ConversationHistory({ onSelect }: Props = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeConvId = searchParams.get('conv');
  const { firstInteraction } = useChatMessages();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string>();

  // Rename dialog
  const [renameId, setRenameId] = useState<string>();
  const [renameTitle, setRenameTitle] = useState('');

  // Delete dialog
  const [deleteId, setDeleteId] = useState<string>();

  const fetchPage = async (offset: number, append = false, silent = false) => {
    try {
      if (offset === 0 && !append) {
        // silent：常駐側欄的背景刷新，保留現有清單不閃 Loader
        if (!silent) setIsFetching(true);
      } else setIsLoadingMore(true);

      const result = await conversationsApi.list(PAGE_SIZE, offset);
      setTotal(result.total);
      setItems((prev) => (append ? [...prev, ...result.items] : result.items));
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : '載入失敗');
    } finally {
      setIsFetching(false);
      setIsLoadingMore(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchPage(0);
  }, []);

  // 常駐側欄的清單刷新（皆為 silent，不閃 Loader）：
  // 1. 路由變化（切換對話／切頁）
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchPage(0, false, true);
  }, [location.pathname, location.search]);

  // 2. 新對話送出第一句話後（後端此時才 INSERT conversation），稍等 DB 寫入再刷新
  useEffect(() => {
    if (!firstInteraction) return;
    const t = setTimeout(() => fetchPage(0, false, true), 1500);
    return () => clearTimeout(t);
  }, [firstInteraction]);

  // 3. 視窗重新聚焦
  useEffect(() => {
    const onFocus = () => fetchPage(0, false, true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Infinite scroll
  const handleScroll = () => {
    if (!scrollRef.current || isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20 && items.length < total) {
      fetchPage(items.length, true);
    }
  };

  const handleRename = async () => {
    if (!renameId || !renameTitle.trim()) return;
    try {
      await conversationsApi.rename(renameId, renameTitle.trim());
      setItems((prev) =>
        prev.map((c) => (c.id === renameId ? { ...c, title: renameTitle.trim() } : c))
      );
      toast.success('已重新命名');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '重命名失敗');
    } finally {
      setRenameId(undefined);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await conversationsApi.delete(deleteId);
      setItems((prev) => prev.filter((c) => c.id !== deleteId));
      setTotal((t) => t - 1);
      toast.success('對話已刪除');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '刪除失敗');
    } finally {
      setDeleteId(undefined);
    }
  };

  if (isFetching) {
    return (
      <SidebarContent>
        <div className="flex items-center justify-center p-4">
          <Loader />
        </div>
      </SidebarContent>
    );
  }

  if (error) {
    return (
      <SidebarContent>
        <p className="m-3 text-sm text-destructive">{error}</p>
      </SidebarContent>
    );
  }

  const groups = groupByDate(items);
  const groupKeys = [
    ...GROUP_ORDER.filter((k) => groups[k]),
    ...Object.keys(groups)
      .filter((k) => !GROUP_ORDER.includes(k))
      .sort((a, b) => b.localeCompare(a)),
  ];

  return (
    <>
      {/* Rename dialog */}
      <Dialog open={!!renameId} onOpenChange={() => setRenameId(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>重新命名對話</DialogTitle>
          </DialogHeader>
          <Input
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameId(undefined)}>
              取消
            </Button>
            <Button onClick={handleRename}>確認</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>刪除對話</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原，對話中的所有訊息將一併刪除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>確認刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SidebarContent onScroll={handleScroll} ref={scrollRef} className="custom-scrollbar">
        {items.length === 0 ? (
          <p className="m-3 text-sm text-muted-foreground">尚無對話紀錄</p>
        ) : (
          groupKeys.map((group) => (
            <SidebarGroup key={group}>
              <SidebarGroupLabel>{group}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {groups[group].map((conv) => (
                    <SidebarMenuItem key={conv.id} className="group/item">
                      <button
                        className={cn(
                          'relative flex h-9 w-full items-center gap-2 overflow-hidden rounded-md px-2 text-left text-sm hover:bg-accent hover:text-accent-foreground pr-8',
                          conv.id === activeConvId && 'bg-accent text-accent-foreground'
                        )}
                        onClick={() => {
                          navigate(`/chat?conv=${conv.id}`);
                          onSelect?.();
                        }}
                      >
                        <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate text-sm">{conv.title}</span>
                      </button>

                      {/* Context menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover/item:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start">
                          <DropdownMenuItem
                            onClick={() => {
                              setRenameId(conv.id);
                              setRenameTitle(conv.title);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            重新命名
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteId(conv.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            刪除
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
        {isLoadingMore && (
          <div className="flex justify-center p-2">
            <Loader />
          </div>
        )}
      </SidebarContent>
    </>
  );
}
